import React, { useRef, useEffect } from "react";
import Chart from "chart.js";

import useTranslation from "src/translations/useTranslation";
import { TravelAnalysisMessages } from "@shared/languages/interfaces";

import { TravelAnalysisGroupedResult } from "../../types";
import { SUCCESS_RATIO_COLORS } from "../../constants";
import { ChartContainer } from "../../styles";

/** Props for the SuccessGroupedChart component. */ 
interface SuccessGroupedChartProps {
  title: string;
  data: TravelAnalysisGroupedResult | null;
  loading: boolean;
  onBarClick?: (label: string) => void;
}

/**
 * Stacked vertical bar chart with an average success rate line overlay.
 * Displays success (green) + alerts (red) stacked, with y-axis as percentage.
 * Reusable for day, plant, and regional groupings.
 */
const SuccessGroupedChart: React.FC<SuccessGroupedChartProps> = ({ title, data, loading, onBarClick }) => {

  const { t } = useTranslation();
  const chartRef = useRef<Chart | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    if (!canvasRef.current || loading || !data || data.items.length === 0) return;

    const labels = data.items.map((item) =>
      item.label.length > 15 ? `${item.label.substring(0, 15)}…` : item.label
    );

    const successRates = data.items.map((item) => item.successRate);
    const alertRates = data.items.map((item) =>
      item.totalAnalyzed > 0
        ? Math.round((item.alertCount / item.totalAnalyzed) * 10000) / 100
        : 0
    );

    // Average line data (same value for all points)
    const averageData = data.items.map(() => data.averageSuccessRate);

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: t(TravelAnalysisMessages.success),
            data: successRates,
            backgroundColor: SUCCESS_RATIO_COLORS.success,
            stack: "stack1",
            order: 2
          },
          {
            label: t(TravelAnalysisMessages.alerts),
            data: alertRates,
            backgroundColor: SUCCESS_RATIO_COLORS.alerts,
            stack: "stack1",
            order: 2
          },
          {
            label: t(TravelAnalysisMessages.averageLine),
            data: averageData,
            type: "line",
            fill: false,
            borderColor: "#1976d2",
            borderWidth: 2,
            borderDash: [6, 3],
            pointRadius: 0,
            pointHoverRadius: 4,
            order: 1
          } as any
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_event: any, elements: any[]) => {
          if (!onBarClick || !elements || elements.length === 0) return;
          const idx = elements[0]._index;
          const item = data.items[idx];

          if (item && item.alertCount > 0) {
            onBarClick(item.label);
          }
        },
        hover: {
          onHover: (event: any, elements: any[]) => {
            if (!onBarClick) return;
            const target = event?.target as HTMLElement;

            if (target) {
              target.style.cursor = elements.length > 0 ? "pointer" : "default";
            }
          }
        },
        legend: {
          position: "bottom",
          labels: { boxWidth: 12, fontSize: 11 }
        },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: { display: false }
          }],
          yAxes: [{
            stacked: true,
            ticks: {
              beginAtZero: true,
              max: 100,
              callback: (value: number) => `${value}%`
            },
            gridLines: { drawBorder: false }
          }]
        },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (tooltipItem: any, chartData: any) => {
              const datasetLabel = chartData.datasets[tooltipItem.datasetIndex].label;
              const value = tooltipItem.yLabel;

              return ` ${datasetLabel}: ${value.toFixed(1)}%`;
            },
            afterBody: (_tooltipItems: any[], chartData: any) => {
              // Show absolute counts in tooltip
              const index = _tooltipItems[0]?.index;

              if (index === undefined) return "";

              const item = data.items[index];

              return `\n  Total: ${item.totalAnalyzed} | ✓ ${item.successCount} | ✗ ${item.alertCount}`;
            }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, t]);

  if (!loading && (!data || data.items.length === 0)) {
    return (
      <ChartContainer>
        <div className="chart-title">{title}</div>
        <div className="chart-empty">{t(TravelAnalysisMessages.noGroupedData)}</div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <div className="chart-title">{title}</div>
      <div style={{ position: "relative", height: 300 }}>
        <canvas ref={canvasRef} />
      </div>
    </ChartContainer>
  );
};

export default SuccessGroupedChart;
